import qconsole from 'scripts/lib/qconsole';

export const DEFAULT_APP_ID = 'gladly.com-team-assist';
// The web-v2 dev server (`npm run dev` in chat-sdk/web-v2), which is also the
// fallback the widget's own bootstrap uses. Note :4270 serves the sandbox config,
// not the widget bundle.
export const DEFAULT_CDN = 'https://localhost:4269';
export const CONTAINER_ID = 'gladlyChat_container';
export const PADDING_STYLE_ID = 'gladly-sidebar-body-padding';
const POLL_MS = 1000;
const MAX_LOAD_ATTEMPTS = 3;

/**
 * Embeds the locally-served Gladly chat widget into Agent Desktop, so the Beacon
 * companion can be driven against this app without the dev Chrome extension.
 *
 * The injection contract is the widget's, not ours (see dev-chat-extension's
 * EMBEDDING.md) and the details are load-order-sensitive:
 *
 *   - the script must NOT be a module and must NOT be async or deferred.
 *     Dynamically created scripts default to `async = true`, so it is set back
 *     to false explicitly.
 *   - `init()` must run inside `onload`. `window.Gladly` can already exist from
 *     another Gladly script on the page, and calling `init()` before this
 *     bootstrap finishes loading would drive that other widget instead.
 *   - inject once per full page load. Agent Desktop is a Director SPA, so
 *     client-side navigation must NOT re-inject — the widget owns its own
 *     lifecycle once mounted.
 *
 * Gated on the agent being signed in: the widget must not appear on the login
 * page. `AuthStore` is localStorage-backed rather than a Morearty binding, so
 * there is nothing to subscribe to — hence the poll, which also lets us tear the
 * widget down again on logout.
 *
 * ⚠️ Serve the app on `assist.localhost:8000`, not `localhost:8000`. The widget
 * reports the current page URL as `clientInfo.url` on every outbound message, and
 * a WAF in front of the chat API rejects any body referencing a bare `localhost`
 * or `127.0.0.1` host — every send comes back `403 {"message":"Forbidden"}` with
 * nothing to indicate the body was the problem. Any other `*.localhost` label
 * passes and still resolves to 127.0.0.1; avoid `demo.localhost`, which this
 * codebase reserves for its demo Playwright suite.
 */
export default function configureChatWidget(runnerContext, options = {}) {
  const { appId = DEFAULT_APP_ID, cdn = DEFAULT_CDN, doc = document, win = window, scheduler = win } = options;

  let injected = false;
  let failedAttempts = 0;
  // Bumped on every injection and every teardown, so a bootstrap still in
  // flight when the agent logs out can tell that its load has been abandoned.
  let generation = 0;

  const isLoggedIn = () => {
    try {
      const auth = runnerContext.stores.auth.get();
      return Boolean(auth && auth.isLoggedIn());
    } catch (e) {
      return false;
    }
  };

  function inject() {
    if (injected || failedAttempts >= MAX_LOAD_ATTEMPTS) {
      return;
    }
    injected = true;
    const thisLoad = ++generation;

    const script = doc.createElement('script');
    script.src = `${cdn}/assets/chat-sdk-v2/bootstrap-v2.js`;
    // Must be a classic, non-async script — see the note above.
    script.async = false;
    script.onload = () => {
      failedAttempts = 0;
      // The bootstrap defines window.Gladly as it executes, so by the time this
      // fires the agent may already have logged out and torn the widget down.
      // Mounting anyway would put it on the login page, and the poll could not
      // clean it up.
      if (thisLoad !== generation || !isLoggedIn()) {
        return;
      }
      // Only the appId and CDN are passed: the fetched config already carries the
      // cluster + provider URLs for this appId, so overriding clusterUrl here
      // would fight it.
      win.Gladly.init({ appId, gladlyCdn: cdn });
    };
    script.onerror = () => {
      injected = false;
      failedAttempts += 1;
      const giveUp = failedAttempts >= MAX_LOAD_ATTEMPTS;
      // The widget host serves a self-signed cert, which the browser silently
      // refuses until it has been accepted once. Retry a couple of times (the
      // host may still be starting), then stop — the login poll runs every
      // second, so retrying forever would spam the console indefinitely.
      qconsole.warn(
        `[chat-widget] could not load the widget from ${cdn}` +
          ` (attempt ${failedAttempts}/${MAX_LOAD_ATTEMPTS})${giveUp ? ' — giving up' : ''}. ` +
          `Start it with \`npm run dev\` in chat-sdk/web-v2, ` +
          `then open ${cdn} once to accept its certificate.`
      );
    };
    doc.documentElement.appendChild(script);
  }

  function teardown() {
    if (!injected) {
      return;
    }
    injected = false;
    generation += 1;
    [CONTAINER_ID, PADDING_STYLE_ID].forEach(id => {
      const el = doc.getElementById(id);
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
    delete win.Gladly;
  }

  function sync() {
    if (isLoggedIn()) {
      inject();
    } else {
      teardown();
    }
  }

  sync();
  const timer = scheduler.setInterval(sync, POLL_MS);

  return {
    appId,
    cdn,
    isInjected: () => injected,
    sync,
    stop: () => scheduler.clearInterval(timer),
  };
}
